import { scrollTo } from "./smoothScroll";

/**
 * contactForm — Alpine component for Contact Form plugin Ajax submission.
 *
 * Usage (per plugin docs — x-data and x-validate on the same <form>):
 *   <form x-data="contactForm()" x-validate.validate-on-submit
 *         @submit.prevent="if ($validate.isComplete('formId')) submit($event)">
 *
 * The isComplete() gate lives in the template expression so the plugin can
 * pass the form id as a string, which is how it correctly resolves the form.
 * submit() is only called after all fields pass validation.
 *
 * The form POSTs to the Craft Contact Form plugin action (contact-form/send)
 * via Fetch, expecting JSON responses (Accept: application/json header).
 *
 * Server error keys returned by the plugin:
 *   - errors  — object keyed by attribute name (e.g. { fromEmail: ['...'] })
 *   - message — human-readable summary string
 */
async function getRecaptchaToken(siteKey) {
  if (!siteKey || typeof grecaptcha === "undefined") return null;
  return new Promise((resolve) => {
    grecaptcha.ready(async () => {
      resolve(await grecaptcha.execute(siteKey, { action: "submit" }));
    });
  });
}

export function contactForm() {
  return {
    submitting: false,
    success: false,
    successMessage: "",
    serverErrors: {},
    serverMessage: "",

    async submit(e) {
      // Validation gate is handled in the Alpine expression before this
      // is called ($validate.isComplete('formId')), so we proceed directly.

      this.submitting = true;
      this.serverErrors = {};
      this.serverMessage = "";

      const formEl = e.target;
      const data = new FormData(formEl);

      const recaptchaToken = await getRecaptchaToken(
        formEl.dataset.recaptchaSitekey
      );
      if (recaptchaToken) {
        data.append("g-recaptcha-response", recaptchaToken);
      }

      try {
        const res = await fetch("/", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: data,
        });

        const json = await res.json();

        if (!res.ok) {
          // Contact Form plugin returns errors keyed by attribute name
          this.serverErrors = json.errors ?? {};
          this.serverMessage =
            json.message ?? "Please correct the errors below.";
          this.submitting = false;
        } else {
          this.success = true;
          this.successMessage =
            "Thank you! Your message has been sent. We will be in touch shortly.";
          formEl.reset();

          // Scroll so the form container is centered in the viewport after the
          // success state renders. $nextTick ensures the DOM has settled to its
          // new (shorter) height before we measure, so centering is accurate.
          this.$nextTick(() => {
            const container = formEl.closest(".module-form") ?? formEl;
            const rect = container.getBoundingClientRect();
            const centerY =
              rect.top +
              window.scrollY -
              window.innerHeight / 2 +
              rect.height / 2;

            scrollTo(centerY, { duration: 0.8 });
          });
        }
      } catch {
        this.serverMessage = "An unexpected error occurred. Please try again.";
        this.submitting = false;
      }
    },

    /**
     * Returns true if a given field name has a server-side error.
     * Used in templates to conditionally show server error text.
     */
    hasServerError(fieldName) {
      return (
        Object.prototype.hasOwnProperty.call(this.serverErrors, fieldName) &&
        this.serverErrors[fieldName].length > 0
      );
    },

    /**
     * Returns the first server error string for a field, or empty string.
     */
    getServerError(fieldName) {
      if (this.hasServerError(fieldName)) {
        return this.serverErrors[fieldName][0];
      }
      return "";
    },
  };
}
